/**
 * Redactar: escribir una noticia y mandarla a N periódicos (§10.2).
 *
 * Es la pantalla que justifica el producto, y su diseño responde a una pregunta:
 * ¿qué tiene que hacer alguien con prisa, a las siete de la mañana, para que una
 * nota salga en quince cabeceras? Escribirla, elegir la sección, marcar un grupo
 * y pulsar publicar. Todo lo demás —variantes por sitio, SEO, programación— es
 * opcional y está donde no estorba.
 *
 * El artículo se guarda **antes** de publicar, siempre. Publicar sin guardar
 * dejaría una noticia en los periódicos que no existe en el panel: imposible de
 * corregir, de auditar y de volver a enviar.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useRef, useState } from 'react';
import { AppShell } from '../components/app-shell.tsx';
import { DestinationPicker } from '../components/destination-picker.tsx';
import { ArticleEditor } from '../components/editor.tsx';
import { SiteOverridesPanel } from '../components/site-overrides.tsx';
import { Button, ErrorMessage, Field, Input } from '../components/ui.tsx';
import {
  ApiError,
  articlesApi,
  groupsApi,
  type MediaAsset,
  mediaApi,
  publicationsApi,
  sectionsApi,
  sitesApi,
} from '../lib/api.ts';

/**
 * Zona del navegador, que es la que el usuario tiene en la cabeza al escribir
 * una hora. Se manda junto al instante UTC para poder volver a mostrar
 * "mañana a las 8:00" y no una hora traducida (§17.11).
 */
const browserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

export function ComposePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [contentHtml, setContentHtml] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [featured, setFeatured] = useState<MediaAsset | null>(null);
  const [imageAlt, setImageAlt] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<'now' | 'scheduled'>('now');
  const [scheduledLocal, setScheduledLocal] = useState('');
  const [error, setError] = useState<string | null>(null);

  /**
   * Id del articulo una vez guardado.
   *
   * Las variantes por sitio cuelgan del articulo, asi que hasta que existe no
   * hay donde ponerlas. Guardar tambien es util por si solo: un borrador que
   * sobrevive a cerrar la pestana.
   */
  const [articleId, setArticleId] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const fileInput = useRef<HTMLInputElement>(null);

  const { data: sitesData } = useQuery({ queryKey: ['sites'], queryFn: sitesApi.list });
  const { data: sectionsData } = useQuery({ queryKey: ['sections'], queryFn: sectionsApi.list });
  const { data: groupsData } = useQuery({ queryKey: ['groups'], queryFn: groupsApi.list });
  const { data: coverageData } = useQuery({
    queryKey: ['sections', 'coverage'],
    queryFn: sectionsApi.coverage,
  });

  const sites = sitesData?.sites ?? [];
  const sections = sectionsData?.sections ?? [];
  const groups = groupsData?.groups ?? [];

  const section = sections.find((entry) => entry.id === sectionId) ?? null;

  const mappedSiteIds = useMemo(
    () => new Set(sectionId ? (coverageData?.coverage[sectionId] ?? []) : []),
    [coverageData, sectionId],
  );

  const upload = useMutation({
    mutationFn: (file: File) => mediaApi.upload(file, imageAlt || undefined),
    onSuccess: (asset) => {
      setFeatured(asset);
      setImageAlt(asset.alt ?? '');
      setError(null);
    },
    onError: (cause: unknown) => {
      setError(cause instanceof ApiError ? cause.message : 'No pudimos subir la imagen.');
    },
  });

  /**
   * Guarda la noticia, creandola la primera vez y actualizandola despues.
   *
   * Devuelve el id porque publicar lo necesita: se guarda **siempre** antes de
   * publicar. Al reves —publicar sin guardar— dejaria una noticia en los
   * periodicos que no existe en el panel: imposible de corregir y de auditar.
   */
  const persist = async (status: 'draft' | 'approved'): Promise<string> => {
    const tags = tagsText
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag !== '');

    const body = {
      title: title.trim(),
      contentHtml,
      status,
      excerpt: excerpt.trim() || null,
      sectionId: sectionId || null,
      featuredMediaId: featured?.id ?? null,
      seoTitle: seoTitle.trim() || null,
      seoDescription: seoDescription.trim() || null,
      ...(tags.length > 0 ? { tags } : {}),
    };

    if (articleId) {
      await articlesApi.update(articleId, body);
      return articleId;
    }

    const created = await articlesApi.create(body);
    setArticleId(created.id);
    return created.id;
  };

  const saveDraft = useMutation({
    mutationFn: () => persist('draft'),
    onSuccess: () => {
      setSavedAt(new Date().toLocaleTimeString());
      setError(null);
    },
    onError: (cause: unknown) => {
      setError(cause instanceof ApiError ? cause.message : 'No pudimos guardar el borrador.');
    },
  });

  const publish = useMutation({
    mutationFn: async () => {
      const id = await persist('approved');

      return publicationsApi.create({
        articleId: id,
        siteIds: [...selected],
        mode,
        // `datetime-local` da una hora sin zona. `new Date()` la interpreta en la
        // del navegador, que es la que el usuario tenía en la cabeza, y se envía
        // en UTC junto al nombre de la zona para poder volver a mostrarla igual.
        ...(mode === 'scheduled' && scheduledLocal
          ? { scheduledAt: new Date(scheduledLocal).toISOString(), tz: browserTimeZone() }
          : {}),
      });
    },
    onSuccess: async (publication) => {
      await queryClient.invalidateQueries({ queryKey: ['publications'] });
      await navigate({ to: '/publicaciones/$id', params: { id: publication.id } });
    },
    onError: (cause: unknown) => {
      if (!(cause instanceof ApiError)) {
        setError('Algo falló al publicar.');
        return;
      }

      // El servidor devuelve qué sitios les falta el mapeo. Repetirlo con
      // nombres es más útil que "conflicto": dice exactamente qué arreglar.
      if (cause.code === 'unmapped_sections') {
        const affected = cause.problem?.sites;
        const names = Array.isArray(affected)
          ? affected.map((entry) => (entry as { name?: string }).name ?? '').filter(Boolean)
          : [];

        setError(names.length > 0 ? `${cause.message} Falta en: ${names.join(', ')}.` : cause.message);
        return;
      }

      setError(cause.hint ? `${cause.message} ${cause.hint}` : cause.message);
    },
  });

  const canPublish =
    title.trim() !== '' &&
    contentHtml.trim() !== '' &&
    selected.size > 0 &&
    (mode === 'now' || scheduledLocal !== '');

  return (
    <AppShell title="Redactar" description="Escribe la noticia una vez y elige a qué periódicos va.">
      <ErrorMessage>{error}</ErrorMessage>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        {/* --- La noticia ------------------------------------------------ */}
        <div className="rounded-xl border border-[var(--color-line)] bg-[var(--color-card)] p-6">
          <Field id="titulo" label="Titular">
            <Input
              id="titulo"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="El consejo aprueba el presupuesto"
              maxLength={300}
            />
          </Field>

          <Field
            id="cuerpo-label"
            label="Cuerpo"
            hint="Los enlaces se abren en otra pestaña y no pasan referencia al sitio de origen."
          >
            <ArticleEditor id="cuerpo" value={contentHtml} onChange={setContentHtml} />
          </Field>

          <Field id="extracto" label="Entradilla" hint="Opcional. Es el resumen que muestran los listados.">
            <textarea
              id="extracto"
              value={excerpt}
              onChange={(event) => setExcerpt(event.target.value)}
              rows={2}
              maxLength={1000}
              className="w-full rounded-lg border border-[var(--color-line)] px-3 py-2 text-sm outline-none focus:border-[var(--color-brand)]"
            />
          </Field>

          <details className="mb-4 rounded-lg border border-[var(--color-line)] px-3 py-2">
            <summary className="cursor-pointer text-sm font-medium">SEO</summary>

            <div className="mt-3">
              <Field id="seo-titulo" label="Título SEO" hint="Si se deja vacío, se usa el titular.">
                <Input
                  id="seo-titulo"
                  value={seoTitle}
                  onChange={(event) => setSeoTitle(event.target.value)}
                  maxLength={200}
                />
              </Field>

              <Field id="seo-desc" label="Descripción SEO">
                <Input
                  id="seo-desc"
                  value={seoDescription}
                  onChange={(event) => setSeoDescription(event.target.value)}
                  maxLength={500}
                />
              </Field>

              <p className="text-xs text-[var(--color-muted)]">
                La política de contenido duplicado —canonical al original o noindex en las copias— se
                configura por sitio en Sitios.
              </p>
            </div>
          </details>
        </div>

        {/* --- Metadatos y destinos -------------------------------------- */}
        <div className="rounded-xl border border-[var(--color-line)] bg-[var(--color-card)] p-6 lg:sticky lg:top-6 lg:self-start">
          <Field id="seccion" label="Categoría">
            <select
              id="seccion"
              value={sectionId}
              onChange={(event) => setSectionId(event.target.value)}
              className="w-full rounded-lg border border-[var(--color-line)] px-3 py-2 text-sm outline-none focus:border-[var(--color-brand)]"
            >
              <option value="">Sin categoría</option>
              {sections.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
          </Field>

          <Field id="etiquetas" label="Etiquetas" hint="Separadas por comas.">
            <Input
              id="etiquetas"
              value={tagsText}
              onChange={(event) => setTagsText(event.target.value)}
              placeholder="presupuesto, consejo"
            />
          </Field>

          {/* --- Imagen destacada --------------------------------------- */}
          <div className="mb-4">
            <p className="mb-1.5 block text-sm font-medium">Imagen destacada</p>

            {featured ? (
              <div className="rounded-lg border border-[var(--color-line)] p-2">
                <img
                  src={featured.url}
                  alt={featured.alt ?? ''}
                  className="mb-2 aspect-video w-full rounded object-cover"
                />
                <p className="mb-2 text-xs text-[var(--color-muted)]">
                  {featured.width}×{featured.height} · {Math.round(featured.bytes / 1024)} KB
                </p>

                <Field
                  id="alt"
                  label="Texto alternativo"
                  hint="Describe la imagen para quien no la ve. Los buscadores también lo leen."
                >
                  <Input
                    id="alt"
                    value={imageAlt}
                    onChange={(event) => setImageAlt(event.target.value)}
                    maxLength={300}
                  />
                </Field>

                <Button variant="ghost" onClick={() => setFeatured(null)}>
                  Quitar imagen
                </Button>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-[var(--color-line)] p-4 text-center">
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/avif"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                      upload.mutate(file);
                    }
                  }}
                />
                <Button variant="ghost" loading={upload.isPending} onClick={() => fileInput.current?.click()}>
                  {upload.isPending ? 'Subiendo…' : 'Elegir imagen'}
                </Button>
                <p className="mt-2 text-xs text-[var(--color-muted)]">
                  Cada periódico se descarga su propia copia desde nuestro servidor.
                </p>
              </div>
            )}
          </div>

          {/* --- Destinos ----------------------------------------------- */}
          <div className="mb-4">
            <p className="mb-1.5 block text-sm font-medium">
              Periódicos{' '}
              <span className="font-normal text-[var(--color-muted)]">
                ({selected.size} de {sites.length})
              </span>
            </p>

            {sites.length === 0 ? (
              <p className="rounded-lg bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-muted)]">
                Todavía no hay periódicos conectados.
              </p>
            ) : (
              <DestinationPicker
                sites={sites}
                groups={groups}
                selected={selected}
                onChange={setSelected}
                section={section}
                mappedSiteIds={mappedSiteIds}
              />
            )}
          </div>

          {/* --- Ajustes por sitio ---------------------------------------- */}
          {selected.size > 0 ? (
            <details className="mb-4 rounded-lg border border-[var(--color-line)] px-3 py-2">
              <summary className="cursor-pointer text-sm font-medium">
                Ajustes por sitio{' '}
                <span className="font-normal text-[var(--color-muted)]">
                  (titular propio, contenido duplicado)
                </span>
              </summary>

              <div className="mt-3">
                <p className="mb-3 text-xs text-[var(--color-muted)]">
                  El mismo texto en varios dominios compite consigo mismo en los buscadores. Cambiar el
                  titular y la entradilla en cada cabecera lo mitiga, y además es lo que un editor haría de
                  todas formas.
                </p>

                {articleId ? (
                  <SiteOverridesPanel
                    articleId={articleId}
                    sites={sites.filter((site) => selected.has(site.id))}
                  />
                ) : (
                  /*
                    Las variantes cuelgan del artículo, así que no hay dónde
                    ponerlas hasta que existe. Se dice por qué en vez de enseñar
                    un formulario deshabilitado sin explicación.
                  */
                  <div className="rounded-lg bg-[var(--color-surface)] p-3 text-center">
                    <p className="mb-2 text-xs text-[var(--color-muted)]">
                      Guarda el borrador para poder darle a cada periódico su propio titular.
                    </p>
                    <Button
                      variant="ghost"
                      loading={saveDraft.isPending}
                      disabled={title.trim() === ''}
                      onClick={() => saveDraft.mutate()}
                    >
                      Guardar borrador
                    </Button>
                  </div>
                )}
              </div>
            </details>
          ) : null}

          {/* --- Cuándo -------------------------------------------------- */}
          <fieldset className="mb-4 rounded-lg border border-[var(--color-line)] p-3">
            <legend className="px-1 text-sm font-medium">Cuándo</legend>

            <div className="mb-2 flex gap-4">
              {(
                [
                  ['now', 'Publicar ahora'],
                  ['scheduled', 'Programar'],
                ] as const
              ).map(([value, label]) => (
                <label key={value} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="cuando"
                    value={value}
                    checked={mode === value}
                    onChange={() => setMode(value)}
                  />
                  {label}
                </label>
              ))}
            </div>

            {mode === 'scheduled' ? (
              <>
                <Input
                  type="datetime-local"
                  aria-label="Fecha y hora"
                  value={scheduledLocal}
                  onChange={(event) => setScheduledLocal(event.target.value)}
                />
                <p className="mt-1.5 text-xs text-[var(--color-muted)]">
                  En tu zona ({browserTimeZone()}). Cada periódico lo verá en la suya.
                </p>
              </>
            ) : null}
          </fieldset>

          <Button onClick={() => publish.mutate()} loading={publish.isPending} disabled={!canPublish}>
            {mode === 'scheduled' ? 'Programar' : 'Publicar ahora'}
          </Button>

          <div className="mt-2">
            <Button
              variant="ghost"
              loading={saveDraft.isPending}
              disabled={title.trim() === ''}
              onClick={() => saveDraft.mutate()}
            >
              {savedAt ? `Guardado a las ${savedAt} · guardar de nuevo` : 'Guardar borrador'}
            </Button>
          </div>

          {/*
            No se promete atomicidad (regla #10). Cada periódico es independiente
            y hay que decirlo aquí, no descubrirlo cuando uno falla.
          */}
          <p className="mt-2 text-center text-xs text-[var(--color-muted)]">
            Cada periódico se publica por separado. Si uno falla, los demás siguen.
          </p>
        </div>
      </div>
    </AppShell>
  );
}
